import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, Chip, Button, useMediaQuery, CircularProgress, Tooltip } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ThemeContext } from '../../../lib/contexts/ThemeContext';
import { BenefitDetailsContext } from '../../../lib/contexts/BenefitDetailsContext';
import useBenefitDetails from '../../../lib/hooks/useBenefitDetails';
import { differenceInWeeks } from 'date-fns';
import useSession from '../../../lib/hooks/useSession';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import { getClientHost } from '../../../lib/constants/constants';
import { useIntercom } from 'react-use-intercom';
import SmartTooltipChip from '../../../components/SmartTooltipChip/SmartTooltipChip';

const NewProgramInfoCard = ({ programInfo, providerView = false }) => {
  const isMobileView = useMediaQuery('(max-width:600px)');
  const { mainBenefitDetails } = useContext(BenefitDetailsContext);
  const [effectiveProgramInfo, setEffectiveProgramInfo] = useState(null);
  const [userFacingStatus, setUserFacingStatus] = useState(null);
  const [workflowData, setWorkflowData] = useState({});
  const [type, setType] = useState('');
  const { data: session } = useSession();
  const { boot } = useIntercom();

  useEffect(() => {
    if (session) {
      setWorkflowData({
        currentDietQuality: session?.lastQuality,
        goalDietQuality: session?.goalQuality,
      });
    }
  }, [session]);
  const applyURL = getClientHost() + '/assessment/start';
  // console.log('here is workflow data: ', workflowData);

  const statusMapList = {
    'Lead Created': 'Lead Created', // 'Not Started' or 'In Progress', depending on the progress of the users assessment
    'Eligible Lead': 'In Review',
    'Ready for Re-Auth': 'Ready for Re-Auth', // triggers a push notification to user depending on expiration date
    'Re-Auth Eligible': 'In Review',
    'Re-Auth Verified': 'In Review',
    'Payer Verified': 'In Review',
    'RD Needed': 'In Review',
    'MNT Session Needed': 'Action Required',
    'MNT Scheduled': 'In Review',
    'Ready to Submit': 'In Review',
    'Submitted to Payer': 'Awaiting Approval',
    Activated: 'Active',
    Canceled: 'Canceled',
    Cancelled: 'Canceled',
    Completed: 'Completed',
    Ineligible: 'Not Eligible',
    Waitlisted: 'Waitlisted',
  };

  useEffect(() => {
    if (programInfo) {
      setEffectiveProgramInfo(programInfo);
    } else if (mainBenefitDetails) {
      setEffectiveProgramInfo(mainBenefitDetails);
    } else {
      setEffectiveProgramInfo(null);
    }
    // console.log('here is mainBenefitDetails in newprograminfocard: ', mainBenefitDetails);
  }, [programInfo, mainBenefitDetails]);

  useEffect(() => {
    if (effectiveProgramInfo?.status) {
      let tempStatus = statusMapList[effectiveProgramInfo?.status];
      if (tempStatus === 'Lead Created') {
        if (workflowData.currentDietQuality) {
          tempStatus = 'In Progress';
        } else {
          tempStatus = 'Not Started';
        }
      }
      if (tempStatus === 'Ready for Re-Auth') {
        if (new Date(effectiveProgramInfo?.auth_end_date) > new Date()) {
          tempStatus = 'Active - Time to Renew';
        } else {
          tempStatus = 'Re-Apply';
        }
      }
      setUserFacingStatus(tempStatus);
      if (tempStatus === 'Active' || tempStatus === 'Canceled' || tempStatus === 'Completed') {
        setType('Program');
      } else {
        setType('Application');
      }
    }
  }, [effectiveProgramInfo]);

  const handleMoreInfoClick = (path = 'denied') => {
    if (path === 'denied') {
      window.open(
        'https://help.jointangelo.com/en/articles/10346819-what-happens-if-my-application-is-denied',
        '_blank',
      );
    } else if (path === 'pending') {
      window.open('https://help.jointangelo.com/en/articles/10346696-what-happens-after-i-apply', '_blank');
    }
  };

  const handleScheduleNowClick = () => {
    boot();
    const message = 'Question about my nutrition counseling session';
    window.Intercom('showNewMessage', message);

    // Track the event to trigger workflow steps
    window.Intercom('trackEvent', 'button_clicked_step_3');

    // Update user data to modify workflow stage
    window.Intercom('update', { workflow_stage: 'step_3' });
  };

  const getChip = (chipStatus) => {
    let backgroundColor = 'white';
    let color = '#000000de';
    let fontWeight = 400;
    if (chipStatus === 'Active' || chipStatus === 'Activated' || chipStatus === 'Active - Time to Renew') {
      backgroundColor = '#00735E';
      color = '#FFF';
      fontWeight = 700;
    } else if (chipStatus === 'Not Started' || chipStatus === 'In Review' || chipStatus === 'Awaiting Approval') {
      backgroundColor = '#E1DDED';
    } else if (
      chipStatus === 'In Progress' ||
      chipStatus === 'Completed' ||
      chipStatus === 'Canceled' ||
      chipStatus === 'Not Eligible' ||
      chipStatus === 'Waitlisted'
    ) {
      backgroundColor = '#00000014';
    } else if (chipStatus === 'Action Required' || chipStatus === 'Re-Apply') {
      backgroundColor = '#FEF2A4';
    }
    return (
      <Chip
        label={
          <Typography
            sx={{
              color: color,
              fontFeatureSettings: "'liga' off, 'clig' off",
              fontSize: '13px',
              fontStyle: 'normal',
              fontWeight: fontWeight,
              lineHeight: '18px', // 138.462%
              letterSpacing: '0.16px',
            }}
          >
            {chipStatus}
          </Typography>
        }
        sx={{
          display: 'flex',
          padding: '4px',
          alignItems: 'center',
          borderRadius: '100px',
          background: backgroundColor,
        }}
      />
    );
  };

  let weeksLeft;
  if (effectiveProgramInfo?.auth_end_date) {
    weeksLeft = differenceInWeeks(new Date(effectiveProgramInfo?.auth_end_date), new Date());
  } else {
    weeksLeft = 'Loading...';
  }

  const getLeftBox = () => {
    if (!effectiveProgramInfo) {
      return (
        <Box
          id="program-info-left-box-container"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minWidth: !providerView ? '250px' : '120px',
            height: '100px',
            flex: '1 0 0',
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    return (
      <Box
        id="program-info-left-box-container"
        sx={{
          display: 'flex',
          padding: '18px 24px',
          flexDirection: 'column',
          alignItems: 'flex-start',
          minWidth: !providerView ? '250px' : '120px',
          gap: '14px',
          flex: '1 0 0',
        }}
      >
        <Typography
          sx={{
            display: 'flex',
            width: '135px',
            height: '16px',
            flexDirection: 'column',
            justifyContent: 'center',
            color: 'var(--text-secondary, rgba(0, 0, 0, 0.60))',
            fontFeatureSettings: "'liga' off, 'clig' off",
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '150%', // 24px
            letterSpacing: '0.15px',
          }}
        >
          {providerView ? 'MNT' : 'Your'} {type}
        </Typography>

        <Box
          id="program-name-box"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            alignSelf: 'stretch',
          }}
        >
          <Tooltip title={effectiveProgramInfo?.benefit_name || 'Medically-Tailored Food Program'}>
            <Typography
              sx={{
                flex: '1 0 0',
                color: 'var(--text-primary, rgba(0, 0, 0, 0.87))',
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontSize: '24px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: '133.4%', // 32.016px
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'normal',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {effectiveProgramInfo?.benefit_name
                ? effectiveProgramInfo?.benefit_name
                : 'Medically-Tailored Food Program'}
            </Typography>
          </Tooltip>
        </Box>

        <Box
          id="status-chip-container"
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: '18px',
            alignSelf: 'stretch',
          }}
        >
          {getChip(userFacingStatus)}
          {userFacingStatus === 'Active' && (
            <Typography
              sx={{
                color: 'var(--text-primary, rgba(0, 0, 0, 0.87))',
                fontFeatureSettings: "'liga' off, 'clig' off",
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '175%', // 28px
                letterSpacing: '0.15px',
              }}
            >
              {effectiveProgramInfo?.auth_end_date && weeksLeft} {effectiveProgramInfo?.auth_end_date && 'weeks left'}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const getRightBox = () => {
    if (!userFacingStatus) {
      return (
        <Box
          id="program-info-right-box-container"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100px', // Adjust height as needed
            flex: '1 0 0',
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (userFacingStatus === 'Not Eligible') {
      return (
        <Box
          id="program-benefit-info-not-eligible"
          sx={{
            display: 'flex',
            maxWidth: '380px',
            padding: '24px 24px 18px 24px',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            flex: '1 0 0',
            alignSelf: 'stretch',
            background: 'var(--Gray-gray5, #E8E8E8)',
            borderRadius: isMobileView ? '0px 0px 8px 8px' : '0px 8px 8px 0px',
          }}
        >
          <Box
            id="not-eligible-message"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '20px',
              alignSelf: 'stretch',
            }}
          >
            <Box
              id="not-eligible-message-title"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '12px',
              }}
            >
              <Typography
                sx={{
                  color: 'var(--Gray-gray12, #171717)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontSize: '18px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: 'normal',
                }}
              >
                Application Not Approved
              </Typography>
            </Box>
            <Typography
              sx={{
                alignSelf: 'stretch',
                color: 'var(--Gray-gray12, #171717)',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '17px', // 121.429%
              }}
            >
              Looks like you didn't qualify for the{' '}
              {effectiveProgramInfo?.benefit_name
                ? effectiveProgramInfo?.benefit_name
                : 'Medically-Tailored Food Program'}
            </Typography>
          </Box>
          <Button
            id="more-information-button"
            sx={{
              display: 'flex',
              height: '36px',
              padding: '0px 13px',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '13px',
              alignSelf: 'stretch',
              borderRadius: '7px',
              border: '2px solid #211551',
              background: '#FFF',
            }}
            onClick={() => handleMoreInfoClick('denied')}
          >
            <Typography
              sx={{
                color: 'var(--color12, #1C124D)',
                textAlign: 'center',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal',
              }}
            >
              Re-Apply
            </Typography>
            <ArrowForwardIcon sx={{ width: '20px', height: '20px' }} />
          </Button>
        </Box>
      );
    }
    if (userFacingStatus === 'Waitlisted') {
      return (
        <Box
          id="program-benefit-info-not-eligible"
          sx={{
            display: 'flex',
            maxWidth: '380px',
            padding: '24px 24px 18px 24px',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            flex: '1 0 0',
            alignSelf: 'stretch',
            background: 'var(--Gray-gray5, #E8E8E8)',
            borderRadius: isMobileView ? '0px 0px 8px 8px' : '0px 8px 8px 0px',
          }}
        >
          <Box
            id="not-eligible-message"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '12px',
              alignSelf: 'stretch',
            }}
          >
            <Box
              id="not-eligible-message-title"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '12px',
              }}
            >
              <Typography
                sx={{
                  color: 'var(--Gray-gray12, #171717)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontSize: '18px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: 'normal',
                }}
              >
                You're On the Waitlist
              </Typography>
            </Box>
            <Typography
              sx={{
                alignSelf: 'stretch',
                color: 'var(--Gray-gray12, #171717)',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
              }}
            >
              Your health plan doesn't currently offer a Medically-Tailored Food Program with us, but we're working to
              change that. We'll reach out if they start to offer a program with us!
            </Typography>
          </Box>
        </Box>
      );
    }
    if (userFacingStatus === 'Canceled' || userFacingStatus === 'Completed' || userFacingStatus === 'Active') {
      return (
        <Box
          id="program-benefit-info-eligible"
          sx={{
            display: 'flex',
            maxWidth: '380px',
            padding: '18px 24px',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            alignSelf: 'stretch',
            borderRadius: isMobileView ? '0px 0px 8px 8px' : '0px 8px 8px 0px',
            borderRight: isMobileView ? '2px solid var(--color5, #E1DDED)' : 'none',
            borderBottom: isMobileView ? '2px solid var(--color5, #E1DDED)' : 'none',
            borderLeft: isMobileView ? '2px solid var(--color5, #E1DDED)' : 'none',
            background: userFacingStatus === 'Active' ? 'var(--color4, #EBE8F3)' : 'var(--Gray-gray5, #E8E8E8)',
          }}
        >
          <Box
            id="benefit-info"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '12px',
              alignSelf: 'stretch',
            }}
          >
            <Box
              id="benefit-header"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '12px',
              }}
            >
              <Typography
                sx={{
                  color: 'var(--Gray-gray12, #171717)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: 'normal',
                }}
              >
                Benefit Details
              </Typography>
              <Typography
                sx={{
                  color: 'var(--Gray-gray12, #171717)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontSize: '18px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: 'normal',
                }}
              >
                14 Weeks of Medically-Tailored Food
              </Typography>
            </Box>
          </Box>
          <Box
            id="benefit-dates"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '12px',
              alignSelf: 'stretch',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'row', marginTop: '20px' }}>
              <Typography
                sx={{
                  color: 'var(--Gray-gray12, #171717)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: 'normal',
                }}
              >
                Start Date:
              </Typography>
              <Typography
                sx={{
                  marginLeft: '4px',
                  color: 'var(--Gray-gray12, #171717)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: '17px',
                }}
              >
                {effectiveProgramInfo?.auth_start_date ? effectiveProgramInfo?.auth_start_date : 'N/A'}
              </Typography>
            </div>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <Typography
                sx={{
                  color: 'var(--Gray-gray12, #171717)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: 'normal',
                }}
              >
                End Date:
              </Typography>
              <Typography
                sx={{
                  marginLeft: '4px',
                  color: 'var(--Gray-gray12, #171717)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: '17px',
                }}
              >
                {effectiveProgramInfo?.auth_end_date ? effectiveProgramInfo?.auth_end_date : 'N/A'}
              </Typography>
            </div>
          </Box>
        </Box>
      );
    }
    if (userFacingStatus === 'Not Started') {
      return (
        <Box
          id="program-benefit-info-not-eligible"
          sx={{
            display: 'flex',
            maxWidth: '380px',
            padding: '24px 24px 18px 24px',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            flex: '1 0 0',
            alignSelf: 'stretch',
            gap: 'var(--3, 13px)',
            background: 'var(--color4, #EBE8F3)',
            borderRadius: isMobileView ? '0px 0px 8px 8px' : '0px 8px 8px 0px',
          }}
        >
          <Box
            id="not-eligible-message"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '12px',
              alignSelf: 'stretch',
            }}
          >
            <Box
              id="not-eligible-message-title"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '12px',
              }}
            >
              <Typography
                sx={{
                  color: 'var(--Gray-gray12, #171717)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontSize: '18px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: 'normal',
                }}
              >
                Start Your Application
              </Typography>
            </Box>
            <Typography
              sx={{
                alignSelf: 'stretch',
                color: 'var(--Gray-gray12, #171717)',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '17px', // 121.429%
              }}
            >
              Apply now to see if you qualify for our Medically-Tailored Food Program and access nutritious meals or
              groceries tailored to your health needs.
            </Typography>
          </Box>
          <Button
            id="more-information-button"
            sx={{
              display: 'flex',
              height: 'var(--3, 36px)',
              padding: 'var(--0, 0px) var(--3, 13px)',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 'var(--3, 13px)',
              alignSelf: 'stretch',
              borderRadius: 'var(--3, 7px)',
              border: 'var(--1, 2px) solid var(--color12, #1C124D)',
              background: 'var(--color1, #FDFDFD)',
            }}
            onClick={() => window.open(applyURL, '_blank')}
          >
            <Typography
              sx={{
                color: 'var(--color12, #1C124D)',
                textAlign: 'center',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal',
              }}
            >
              Apply Now
            </Typography>
            <ArrowForwardIcon
              style={{
                width: 'var(--1-5, 24px)',
                height: '24px',
              }}
            />
          </Button>
        </Box>
      );
    }
    if (userFacingStatus === 'In Progress') {
      return (
        <Box
          id="program-benefit-info-not-eligible"
          sx={{
            display: 'flex',
            maxWidth: '380px',
            padding: '24px 24px 18px 24px',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            flex: '1 0 0',
            alignSelf: 'stretch',
            gap: 'var(--3, 13px)',
            background: 'var(--color4, #EBE8F3)',
            borderRadius: isMobileView ? '0px 0px 8px 8px' : '0px 8px 8px 0px',
          }}
        >
          <Box
            id="not-eligible-message"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '12px',
              alignSelf: 'stretch',
            }}
          >
            <Box
              id="not-eligible-message-title"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '12px',
              }}
            >
              <Typography
                sx={{
                  color: 'var(--Gray-gray12, #171717)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontSize: '18px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: 'normal',
                }}
              >
                Finish Application
              </Typography>
            </Box>
            <Typography
              sx={{
                alignSelf: 'stretch',
                color: 'var(--Gray-gray12, #171717)',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '17px', // 121.429%
              }}
            >
              Once complete, our team can check your eligibility for our Medically-Tailored Food Program with your
              health plan.
            </Typography>
          </Box>
          <Button
            id="more-information-button"
            sx={{
              display: 'flex',
              height: 'var(--3, 36px)',
              padding: 'var(--0, 0px) var(--3, 13px)',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 'var(--3, 13px)',
              alignSelf: 'stretch',
              borderRadius: 'var(--3, 7px)',
              border: 'var(--1, 2px) solid var(--color12, #1C124D)',
              background: 'var(--color1, #FDFDFD)',
            }}
            onClick={() => window.open(applyURL, '_blank')}
          >
            <Typography
              sx={{
                color: 'var(--color12, #1C124D)',
                textAlign: 'center',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal',
              }}
            >
              Finish Now
            </Typography>
            <ArrowForwardIcon
              style={{
                width: 'var(--1-5, 24px)',
                height: '24px',
              }}
            />
          </Button>
        </Box>
      );
    }
    if (userFacingStatus === 'Action Required') {
      return (
        <Box
          id="program-benefit-info-not-eligible"
          sx={{
            display: 'flex',
            maxWidth: '380px',
            padding: '24px 24px 18px 24px',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            flex: '1 0 0',
            alignSelf: 'stretch',
            gap: 'var(--3, 13px)',
            background: 'var(--color4, #EBE8F3)',
            borderRadius: isMobileView ? '0px 0px 8px 8px' : '0px 8px 8px 0px',
          }}
        >
          <Box
            id="not-eligible-message"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '12px',
              alignSelf: 'stretch',
            }}
          >
            <Box
              id="not-eligible-message-title"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '12px',
              }}
            >
              <Typography
                sx={{
                  color: 'var(--Gray-gray12, #171717)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontSize: '18px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: 'normal',
                }}
              >
                Meet with Your Tangelo Dietitian
              </Typography>
            </Box>
            <Typography
              sx={{
                alignSelf: 'stretch',
                color: 'var(--Gray-gray12, #171717)',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '17px', // 121.429%
              }}
            >
              Book time with a registered dietitian to review your Diet Quality Results and complete your application.
            </Typography>
          </Box>
          <Button
            id="more-information-button"
            sx={{
              display: 'flex',
              height: 'var(--3, 36px)',
              padding: 'var(--0, 0px) var(--3, 13px)',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 'var(--3, 13px)',
              alignSelf: 'stretch',
              borderRadius: 'var(--3, 7px)',
              border: 'var(--1, 2px) solid var(--color12, #1C124D)',
              background: 'var(--color1, #FDFDFD)',
            }}
            onClick={handleScheduleNowClick}
          >
            <Typography
              sx={{
                color: 'var(--color12, #1C124D)',
                textAlign: 'center',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal',
              }}
            >
              Schedule Now
            </Typography>
            <ArrowForwardIcon
              style={{
                width: 'var(--1-5, 24px)',
                height: '24px',
              }}
            />
          </Button>
        </Box>
      );
    }
    if (userFacingStatus === 'In Review' || userFacingStatus === 'Awaiting Approval') {
      return (
        <Box
          id="program-benefit-info-pending"
          sx={{
            display: 'flex',
            maxWidth: '380px',
            padding: '18px 16px',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '16px',
            alignSelf: 'stretch',
            borderRadius: isMobileView ? '0px 0px 8px 8px' : '0px 8px 8px 0px',
          }}
        >
          <Box
            id="pending-message-container"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '20px',
              alignSelf: 'stretch',
            }}
          >
            <Box
              id="pending-message-box"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '12px',
                alignSelf: 'stretch',
              }}
            >
              <Typography
                sx={{
                  alignSelf: 'stretch',
                  color: 'var(--Gray-gray12, #171717)',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '17px', // 121.429%
                }}
              >
                We're working on confirming your eligibility for our Medically-Tailored Food Program with your health
                plan.
              </Typography>
              <Box
                id="average-response-time-message-box"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 'var(--4-5, 21px)',
                  alignSelf: 'stretch',
                  borderRadius: '8px',
                  background: 'var(--color4, #EBE8F3)',
                }}
              >
                <Box
                  id="average-response-time-message"
                  sx={{
                    display: 'flex',
                    height: '40px',
                    padding: '12px 8px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '4px',
                    alignSelf: 'stretch',
                    borderRadius: '8px',
                  }}
                >
                  <Box
                    id="average-response-time-message-text-container"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <Typography
                      sx={{
                        color: 'var(--Gray-gray12, #171717)',
                        leadingTrim: 'both',
                        textEdge: 'cap',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: 'normal',
                      }}
                    >
                      Avg. Response time:
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      color: 'var(--Gray-gray12, #171717)',
                      leadingTrim: 'both',
                      textEdge: 'cap',
                      fontSize: '18px',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: 'normal',
                    }}
                  >
                    10 days
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          <Button
            id="more-information-button"
            sx={{
              display: 'flex',
              height: 'var(--3, 36px)',
              padding: 'var(--0, 0px) var(--3, 13px)',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 'var(--3, 13px)',
              alignSelf: 'stretch',
              borderRadius: 'var(--3, 7px)',
              border: 'var(--1, 2px) solid var(--color12, #1C124D)',
              background: 'var(--color1, #FDFDFD)',
            }}
            onClick={() => handleMoreInfoClick('pending')}
          >
            <InfoIcon sx={{ width: 'var(--1, 20px)', height: 'var(--1, 20px)' }} />
            <Typography
              sx={{
                color: 'var(--color12, #1C124D)',
                textAlign: 'center',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal',
              }}
            >
              More Information
            </Typography>
          </Button>
        </Box>
      );
    }
    if (userFacingStatus === 'Activated') {
      return (
        <Box
          id="program-benefit-info-not-eligible"
          sx={{
            display: 'flex',
            maxWidth: '380px',
            padding: '24px 24px 18px 24px',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            flex: '1 0 0',
            alignSelf: 'stretch',
            background: '#E8F9F5',
            borderRadius: isMobileView ? '0px 0px 8px 8px' : '0px 8px 8px 0px',
          }}
        >
          <Box
            id="not-eligible-message"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '12px',
              alignSelf: 'stretch',
            }}
          >
            <Box
              id="not-eligible-message-title"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '12px',
              }}
            >
              <Typography
                sx={{
                  color: 'var(--Gray-gray12, #171717)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontSize: '18px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: 'normal',
                }}
              >
                Application Accepted
              </Typography>
            </Box>
            <Typography
              sx={{
                alignSelf: 'stretch',
                color: 'var(--Gray-gray12, #171717)',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
              }}
            >
              Your application was accepted by your health plan and you have been activated in a Medically-Tailored Food
              program as of the data above.
            </Typography>
          </Box>
        </Box>
      );
    }
    if (userFacingStatus === 'Re-Apply') {
      return (
        <Box
          id="program-benefit-info-not-eligible"
          sx={{
            display: 'flex',
            maxWidth: '380px',
            padding: '24px 24px 18px 24px',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            flex: '1 0 0',
            alignSelf: 'stretch',
            gap: 'var(--3, 13px)',
            background: 'var(--color4, #EBE8F3)',
            borderRadius: isMobileView ? '0px 0px 8px 8px' : '0px 8px 8px 0px',
          }}
        >
          <Box
            id="not-eligible-message"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '12px',
              alignSelf: 'stretch',
            }}
          >
            <Box
              id="not-eligible-message-title"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '12px',
              }}
            >
              <Typography
                sx={{
                  color: 'var(--Gray-gray12, #171717)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontSize: '18px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: 'normal',
                }}
              >
                Start Your Next Application
              </Typography>
            </Box>
            <Typography
              sx={{
                alignSelf: 'stretch',
                color: 'var(--Gray-gray12, #171717)',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '17px', // 121.429%
              }}
            >
              Great job completing you last program! Now that it has come to an end, you can apply for another one!
            </Typography>
          </Box>
          <Button
            id="more-information-button"
            sx={{
              display: 'flex',
              height: 'var(--3, 36px)',
              padding: 'var(--0, 0px) var(--3, 13px)',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 'var(--3, 13px)',
              alignSelf: 'stretch',
              borderRadius: 'var(--3, 7px)',
              border: 'var(--1, 2px) solid var(--color12, #1C124D)',
              background: 'var(--color1, #FDFDFD)',
            }}
            onClick={() => window.open(applyURL, '_blank')}
          >
            <Typography
              sx={{
                color: 'var(--color12, #1C124D)',
                textAlign: 'center',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal',
              }}
            >
              Re-Apply Now
            </Typography>
            <ArrowForwardIcon
              style={{
                width: 'var(--1-5, 24px)',
                height: '24px',
              }}
            />
          </Button>
        </Box>
      );
    }
    return (
      <Box
        id="program-info-right-box-container"
        sx={{
          display: 'flex',
          padding: '20px 16px',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '16px',
          flex: '1 0 0',
          background: '#EBE8F3', // this should be variable
          borderRadius: isMobileView ? '0px 0px 8px 8px' : '0px 8px 8px 0px',
        }}
      ></Box>
    );
  };

  return (
    <Box
      id="new-programinfocard-container"
      sx={{
        display: 'flex',
        flexDirection: isMobileView ? 'column' : 'row',
        width: '100%',
        minWidth: !providerView ? '280px' : '120px',
        maxWidth: !providerView ? (isMobileView ? '327px' : '691px') : isMobileView ? '163px' : '327px',
        minHeight: !providerView && isMobileView ? '350px' : '100%',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '8px',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        background: '#FFF',
        height: '100%',
      }}
    >
      {!effectiveProgramInfo || !userFacingStatus ? (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            height: '100%',
            paddingTop: '8%',
            paddingBottom: '8%',
          }}
        >
          <Typography sx={{ fontSize: '16px', fontWeight: 700, lineHeight: 'normal' }}>
            Checking Your Eligibility...
          </Typography>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {getLeftBox()}
          {!providerView && getRightBox()}
        </>
      )}
    </Box>
  );
};

export default NewProgramInfoCard;

export const determineUserFacingStatus = (programInfo, workflowData) => {
  const statusMapList = {
    'Lead Created': 'Lead Created',
    'Eligible Lead': 'In Review',
    'Ready for Re-Auth': 'Ready for Re-Auth',
    'Re-Auth Eligible': 'In Review',
    'Re-Auth Verified': 'In Review',
    'Payer Verified': 'In Review',
    'RD Needed': 'In Review',
    'MNT Session Needed': 'Action Required',
    'MNT Scheduled': 'In Review',
    'Ready to Submit': 'In Review',
    'Submitted to Payer': 'Awaiting Approval',
    Activated: 'Activated',
    Canceled: 'Canceled',
    Completed: 'Completed',
    Ineligible: 'Not Eligible',
    Canceled: 'Canceled',
    Waitlisted: 'Waitlisted',
  };

  if (!programInfo) return null;

  let tempStatus = statusMapList[programInfo.status];
  if (tempStatus === 'Lead Created') {
    if (workflowData.goalDietQuality) {
      tempStatus = 'In Progress';
    } else if (!workflowData.currentDietQuality) {
      tempStatus = 'Not Started';
    }
  }
  if (tempStatus === 'Ready for Re-Auth') {
    if (new Date(programInfo.auth_end_date) > new Date()) {
      tempStatus = 'Active - Time to Renew';
    } else {
      tempStatus = 'Re-Apply';
    }
  }
  return tempStatus;
};
